// authFetch — drop-in fetch wrapper that injects the Cognito ID token from
// sessionStorage and, on a 401, asks the registered refresher to refresh the
// token, then retries the request exactly once.
//
// This is a plain ES module (NOT a React hook). AuthContext registers its
// `refreshSession` function via `registerAuthRefresher` so this module can
// bridge React-state-driven token refresh to bare API clients.

let refresherFn = null;

// Coalesce concurrent 401 refresh attempts: any number of in-flight authFetch
// calls that hit a 401 at the same time will await the same Promise instead
// of triggering N independent refreshes.
let inFlightRefresh = null;

/**
 * Register the function used to refresh the Cognito session.
 * The refresher must:
 *   - Update `sessionStorage.cognitoIdToken` (and refresh token) on success.
 *   - Resolve to `true` on success, `false` on failure.
 * Typically called from AuthContext with `refreshSession`.
 * @param {() => Promise<boolean>} refreshFn
 */
export const registerAuthRefresher = (refreshFn) => {
  refresherFn = typeof refreshFn === "function" ? refreshFn : null;
};

/**
 * Clear the registered refresher (e.g. on AuthProvider unmount).
 */
export const unregisterAuthRefresher = () => {
  refresherFn = null;
  inFlightRefresh = null;
};

/**
 * Read the current Cognito ID token from sessionStorage.
 * @returns {string|null}
 */
export const getAuthToken = () =>
  (typeof window !== "undefined" && window.sessionStorage
    ? sessionStorage.getItem("cognitoIdToken")
    : null);

/**
 * Build a Headers-compatible object with Authorization injected, preserving
 * any caller-provided headers (and not stomping Content-Type).
 */
const withAuthHeaders = (init, token) => {
  const baseHeaders = init && init.headers ? init.headers : undefined;
  // Use the Headers constructor so we accept Headers, plain objects, or
  // [name, value][] arrays consistently.
  const headers = new Headers(baseHeaders || undefined);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};

/**
 * Trigger (or join) a single in-flight refresh. Returns true/false.
 */
const refreshOnce = async () => {
  if (!refresherFn) return false;
  if (inFlightRefresh) {
    return inFlightRefresh;
  }
  inFlightRefresh = (async () => {
    try {
      const ok = await refresherFn();
      return !!ok;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("authFetch refresher threw:", e);
      return false;
    } finally {
      // Allow subsequent 401s to trigger a fresh refresh attempt.
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
};

/**
 * Drop-in replacement for `fetch` that injects the Cognito Bearer token and
 * transparently retries once on 401 after refreshing the session.
 *
 * Notes:
 *   - Does NOT throw on 401 itself; callers inspect `response.ok`.
 *   - If no token is present in sessionStorage at call time, the request is
 *     issued as-is (caller-provided Authorization, if any, is preserved).
 *   - Init is forwarded unchanged except for header injection.
 *
 * @param {string|URL} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, init = {}) => {
  const token = getAuthToken();

  // No token at all: just forward the request — let the server reject it.
  // (We intentionally do not attempt a refresh here; refresh requires the
  // Cognito refresh-token plumbing which only runs once a session exists.)
  if (!token) {
    return fetch(url, init);
  }

  const firstHeaders = withAuthHeaders(init, token);
  const firstInit = { ...init, headers: firstHeaders };
  const response = await fetch(url, firstInit);

  if (response.status !== 401) {
    return response;
  }

  // 401 — try to refresh once (coalesced across concurrent callers).
  if (!refresherFn) {
    return response;
  }

  const refreshed = await refreshOnce();
  if (!refreshed) {
    return response;
  }

  // Re-read the token; the refresher updates sessionStorage on success.
  const newToken = getAuthToken();
  const retryHeaders = withAuthHeaders(init, newToken);
  const retryInit = { ...init, headers: retryHeaders };
  return fetch(url, retryInit);
};

export default authFetch;
